import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Tag,
  Typography,
  Button,
  message,
  Card,
  Space,
  Tabs,
  Avatar,
  Drawer,
  Descriptions,
  Row,
  Col,
  Select,
  Input,
  InputNumber,
  Popconfirm,
  Rate,
  Image,
  Timeline,
  Statistic,
  Badge,
  Dropdown,
  Modal,
  Form,
  Empty,
  Switch,
} from "antd";
import {
  SolutionOutlined,
  ReloadOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  SearchOutlined,
  TrophyOutlined,
  GlobalOutlined,
  LinkedinOutlined,
  YoutubeOutlined,
  InstagramOutlined,
  FacebookOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  PictureOutlined,
  VideoCameraOutlined,
  SafetyCertificateOutlined,
  FileTextOutlined,
  StarFilled,
  TeamOutlined,
  BankOutlined,
  ReadOutlined,
  BulbOutlined,
  TranslationOutlined,
  ScheduleOutlined,
  InfoCircleOutlined,
  BellOutlined,
  MoreOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { API_ENDPOINTS, apiFetch } from "../../services/api";

const { Title, Text, Link, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

// AntD's icon set has no Rupee glyph, so render the ₹ symbol icon-style instead
// (used everywhere price/commercials are shown, in place of DollarCircleOutlined).
const RupeeIcon: React.FC<{ style?: React.CSSProperties; className?: string }> = ({ style, className }) => (
  <span
    className={className}
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "1em",
      fontWeight: 700,
      lineHeight: 1,
      ...style,
    }}
  >
    ₹
  </span>
);

// --- CONFIGURATION ---
const LIST_TRAINER_URL = API_ENDPOINTS.trainers;
const VERIFY_TRAINER_URL_BASE = API_ENDPOINTS.trainer();

interface AwardItem {
  title: string;
  organisation: string;
  year: number;
  category: string;
  description: string;
}

interface ExperienceItem {
  jobTitle: string;
  organisation: string;
  startYear: number;
  endYear: number;
  employmentType: string;
  industry: string;
  achievements: string;
}

interface EducationItem {
  qualification: string;
  institution: string;
  yearOfCompletion: number;
  certificateName?: string;
  issuingOrganisation?: string;
  yearObtained?: number;
}

interface WorkshopItem {
  companyName: string;
  title: string;
  duration: string;
  location: string;
  industry: string;
  domain: string;
  competency: string;
  totalParticipants: number;
  summary: string;
  photos?: string[];
}

interface LanguageItem {
  language: string;
  proficiencyLevel: string;
}

interface Trainer {
  _id: string;
  name: string;
  subjectLine?: string;
  tagLine?: string;
  entityType?: string;
  profilePhoto?: string;
  bannerImage?: string;
  mobile?: string;
  whatsappNumber?: string;
  country?: string;
  state?: string;
  city?: string;
  website?: string;
  linkedinProfile?: string;
  youtubeChannel?: string;
  instagramHandle?: string;
  facebookPage?: string;
  industrySectorExpertise?: string[];
  domainDepartmentalExpertise?: string[];
  competencyExpertise?: string[];
  trainerType?: string;
  profileSummary?: string;
  profileDocument?: string;
  certificates?: string[];
  galleryPhotos?: string[];
  videoUrls?: string[];
  awards?: AwardItem[];
  experience?: ExperienceItem[];
  education?: EducationItem[];
  workshops?: WorkshopItem[];
  openToTravel?: string;
  languagesFluent?: LanguageItem[];
  trainerCertifications?: string[];
  trainingExperienceLevel?: string;
  audienceSeniorityLevel?: string;
  commercialsFeeRange?: string;
  skills?: string[];
  pricePerHour?: number;
  experienceYears?: number;
  mode?: string;
  availableThisWeek?: boolean;
  offersTrialSessions?: boolean;
  rating?: number;
  isVerified?: boolean;
  isProfileVerified?: boolean;
  createdAt?: string;
}

const TRAINER_STYLES = `
  .trainer-page {
    --trainer-blue: #2563eb;
    --trainer-ink: #172033;
    font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }
  .trainer-simple-heading {
    display: flex; align-items: center; justify-content: space-between; gap: 16px;
    margin-bottom: 16px; padding: 4px 2px;
  }
  .trainer-simple-heading h1 { margin: 0; color: #172033; font-size: 24px; line-height: 1.25; font-weight: 750; }
  .trainer-title-row { display: flex; align-items: center; gap: 9px; flex-wrap: wrap; }
  .trainer-source-badge { margin: 0; border-color: #ffd591; color: #ad4e00; background: #fff7e6; font-size: 11px; font-weight: 650; }
  .trainer-simple-heading p { margin: 4px 0 0; color: #7b879b; font-size: 13px; }
  .trainer-summary-row {
    display: grid; grid-template-columns: repeat(2, minmax(220px, 1fr));
    gap: 16px; margin-bottom: 18px;
  }
  .trainer-summary-card {
    position: relative; overflow: hidden; display: flex; align-items: center; gap: 14px;
    min-height: 88px; padding: 17px 19px; border: 1px solid #e8edf5; border-radius: 15px;
    background: #fff; box-shadow: 0 5px 18px rgba(31,45,75,.06);
  }
  .trainer-summary-card::after {
    content: ''; position: absolute; width: 90px; height: 90px; right: -38px; top: -42px;
    border-radius: 50%; background: #eff6ff;
  }
  .trainer-summary-icon {
    display: grid; place-items: center; width: 46px; height: 46px; flex: 0 0 46px;
    border-radius: 13px; color: #fff; font-size: 19px;
    box-shadow: inset 0 1px 0 rgba(255,255,255,.28), 0 7px 14px rgba(15,23,42,.12);
  }
  .trainer-summary-label { color: #64748b; font-size: 12px; font-weight: 600; }
  .trainer-summary-value { margin-top: 1px; color: #172033; font-size: 25px; line-height: 1.1; font-weight: 750; }
  .trainer-summary-caption { margin-top: 3px; color: #9aa5b5; font-size: 11px; }
  .trainer-page-header {
    position: relative; overflow: hidden; display: flex; align-items: center;
    justify-content: space-between; gap: 20px; margin-bottom: 18px; padding: 24px 26px;
    border-radius: 18px; color: #fff;
    background: linear-gradient(120deg, #0f3f9f 0%, #2563eb 58%, #5b8def 100%);
    box-shadow: 0 14px 32px rgba(37, 99, 235, 0.18);
  }
  .trainer-page-header::before, .trainer-page-header::after {
    content: ''; position: absolute; border-radius: 999px; background: rgba(255,255,255,.09);
  }
  .trainer-page-header::before { width: 220px; height: 220px; right: 7%; top: -155px; }
  .trainer-page-header::after { width: 140px; height: 140px; right: -35px; bottom: -92px; }
  .trainer-page-heading, .trainer-header-meta { position: relative; z-index: 1; }
  .trainer-eyebrow { font-size: 11px; font-weight: 700; letter-spacing: 1.4px; text-transform: uppercase; opacity: .72; }
  .trainer-page-heading h1 { margin: 4px 0; color: #fff; font-size: 25px; line-height: 1.2; font-weight: 750; }
  .trainer-page-heading p { margin: 0; color: rgba(255,255,255,.75); font-size: 13px; }
  .trainer-header-meta { display: flex; gap: 10px; flex-wrap: wrap; }
  .trainer-header-chip { padding: 9px 13px; border: 1px solid rgba(255,255,255,.2); border-radius: 10px; background: rgba(255,255,255,.12); backdrop-filter: blur(8px); font-size: 12px; font-weight: 600; }
  .trainer-header-banner {
    background: linear-gradient(135deg, #1677ff 0%, #0b4fc9 100%);
    border-radius: 14px;
    padding: 18px 26px;
    margin-bottom: 18px;
    color: #fff;
    box-shadow: 0 8px 24px rgba(22, 119, 255, 0.25);
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px;
  }
  .trainer-header-banner::after {
    content: '';
    position: absolute;
    right: -40px;
    top: -60px;
    width: 200px;
    height: 200px;
    border-radius: 50%;
    background: rgba(255,255,255,0.08);
  }
  .trainer-header-search.ant-input-affix-wrapper {
    background: rgba(255,255,255,0.92);
    border: none;
    border-radius: 8px;
  }
  .trainer-bell-btn {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: rgba(255,255,255,0.16);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 17px;
    cursor: pointer;
    transition: background 0.15s;
  }
  .trainer-bell-btn:hover {
    background: rgba(255,255,255,0.28);
  }
  .trainer-notif-panel {
    width: 300px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 10px 28px rgba(16,24,40,0.14);
    padding: 10px;
    max-height: 360px;
    overflow-y: auto;
  }
  .trainer-notif-header {
    font-size: 12.5px;
    font-weight: 600;
    color: #1f2937;
    padding: 4px 6px 8px;
  }
  .trainer-notif-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 6px;
    border-radius: 8px;
    cursor: pointer;
  }
  .trainer-notif-item:hover {
    background: #f3f6fb;
  }
  .trainer-notif-viewall {
    text-align: center;
    font-size: 12px;
    color: #1677ff;
    padding: 8px 6px 2px;
    cursor: pointer;
    border-top: 1px solid #f0f0f0;
    margin-top: 4px;
  }
  .trainer-stat-label {
    font-size: 12px;
    color: #64748b;
    font-weight: 600;
  }
  .trainer-stat-value {
    font-size: 24px;
    font-weight: 750;
    color: var(--trainer-ink);
    line-height: 1.3;
  }
  .trainer-stat-caption {
    font-size: 11.5px;
    color: #9ca3af;
  }
  .trainer-filter-bar {
    background: #f8fafc;
    border: 1px solid #e8edf5;
    border-radius: 14px;
    padding: 14px;
    margin-bottom: 16px;
  }
  .trainer-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 14px; margin-bottom: 14px; }
  .trainer-search { width: min(390px, 100%); }
  .trainer-search.ant-input-affix-wrapper { height: 40px; border-radius: 10px; border-color: #dfe6f0; box-shadow: 0 2px 5px rgba(15,23,42,.03); }
  .trainer-results-label { color: #64748b; font-size: 12px; font-weight: 500; white-space: nowrap; }
  .trainer-filter-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(135px, 1fr));
    gap: 10px;
    align-items: end;
  }
  @media (max-width: 1300px) {
    .trainer-filter-grid { grid-template-columns: repeat(3, 1fr); }
  }
  @media (max-width: 700px) {
    .trainer-filter-grid { grid-template-columns: repeat(2, 1fr); }
  }
  .trainer-filter-field label {
    display: block;
    font-size: 11.5px;
    color: #6b7280;
    margin: 0 0 5px 2px;
    font-weight: 600;
  }
  .trainer-filter-field .ant-select-selector, .trainer-filter-field .ant-input-number { border-radius: 8px !important; border-color: #dfe6f0 !important; }
  .trainer-filter-actions {
    display: flex;
    gap: 8px;
    white-space: nowrap;
  }
  .trainer-table.ant-table-wrapper {
    border: 1px solid #e5eaf1;
    border-radius: 12px;
    overflow: hidden;
  }
  .trainer-table .ant-table {
    border-radius: 0;
    color: #334155;
  }
  .trainer-table .ant-table-thead > tr > th {
    background: #f7f9fc !important;
    color: #4b5565;
    font-weight: 650;
    font-size: 12px;
    text-transform: none;
    letter-spacing: 0;
    padding: 13px 14px !important;
    border-bottom: 1px solid #e5eaf1 !important;
  }
  .trainer-table .ant-table-thead > tr > th::before {
    display: none !important;
  }
  .trainer-table .ant-table-tbody > tr > td {
    border-bottom: 1px solid #edf0f4;
    padding: 13px 14px !important;
    color: #334155;
    vertical-align: middle;
  }
  .trainer-table .ant-table-tbody > tr:hover > td {
    background: #f8fbff !important;
  }
  .trainer-table .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }
  .trainer-verified-pill {
    border-radius: 6px !important;
    padding: 1px 8px !important;
    font-size: 11px;
    font-weight: 550;
  }
  .trainer-profile-card {
    display: flex;
    gap: 22px;
    background: #fff;
    border: 1px solid #eef1f6;
    border-radius: 14px;
    padding: 18px 20px;
  }
  .trainer-profile-left {
    flex: 1 1 380px;
    min-width: 0;
  }
  .trainer-profile-top {
    display: flex;
    gap: 16px;
    align-items: flex-start;
  }
  .trainer-name-row {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }
  .trainer-verified-badge {
    color: #1677ff;
    font-size: 16px;
  }
  .trainer-meta-row {
    display: flex;
    flex-wrap: wrap;
    row-gap: 6px;
    column-gap: 16px;
    margin-top: 10px;
    color: #6b7280;
    font-size: 12.5px;
  }
  .trainer-meta-row .anticon {
    margin-right: 6px;
    color: #9ca3af;
  }
  .trainer-social-row {
    display: flex;
    gap: 8px;
    margin-top: 12px;
  }
  .trainer-social-btn {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f3f4f6;
    color: #4b5563 !important;
    transition: background 0.15s, transform 0.15s;
  }
  .trainer-social-btn:hover {
    background: #e5e7eb;
    transform: translateY(-2px);
  }
  .trainer-profile-banner {
    flex: 1 1 320px;
    border-radius: 12px;
    overflow: hidden;
    min-height: 168px;
    background: linear-gradient(135deg, #1677ff 0%, #0b4fc9 100%);
  }
  .trainer-profile-banner img {
    width: 100%;
    height: 100%;
    min-height: 168px;
    object-fit: cover;
    display: block;
  }
  .trainer-quickfacts-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 18px;
    background: #fff;
    border: 1px solid #eef1f6;
    border-radius: 14px;
    padding: 14px 20px;
    margin-top: 14px;
  }
  .trainer-quickfact-label {
    font-size: 11.5px;
    color: #9ca3af;
    margin-bottom: 2px;
  }
  .trainer-quickfact-value {
    font-size: 13.5px;
    font-weight: 600;
    color: #111827;
  }
  .trainer-stats-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 14px;
    margin-top: 14px;
  }
  @media (max-width: 1000px) {
    .trainer-stats-grid { grid-template-columns: repeat(3, 1fr); }
  }
  .trainer-grid-3 {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
    margin-top: 14px;
  }
  @media (max-width: 1000px) {
    .trainer-grid-3 { grid-template-columns: 1fr; }
  }
  .trainer-panel {
    background: #fff;
    border: 1px solid #eef1f6;
    border-radius: 14px;
    padding: 16px 18px;
    height: 100%;
  }
  .trainer-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
  }
  .trainer-panel-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    color: #1f2937;
    font-size: 13.5px;
  }
  .trainer-panel-viewall {
    font-size: 12px;
    color: #1677ff;
    cursor: pointer;
  }
  .trainer-panel-empty {
    color: #9ca3af;
    font-size: 12.5px;
  }
  .trainer-doc-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
  }
  .trainer-doc-tile {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 12px 6px;
    border-radius: 10px;
    background: #f7f9fc;
    cursor: pointer;
    transition: background 0.15s;
  }
  .trainer-doc-tile:hover {
    background: #eef2ff;
  }
  .trainer-doc-tile.disabled {
    cursor: default;
    opacity: 0.5;
  }
  .trainer-doc-icon {
    width: 38px;
    height: 38px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: #fff;
  }
  .trainer-doc-badge {
    position: absolute;
    top: 4px;
    right: 10px;
    background: #1677ff;
    color: #fff;
    border-radius: 10px;
    font-size: 10.5px;
    padding: 0 5px;
    line-height: 16px;
    min-width: 16px;
    text-align: center;
  }
  .trainer-doc-label {
    font-size: 11.5px;
    color: #4b5563;
  }
  .trainer-bottom-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 22px;
    background: #fff;
    border: 1px solid #eef1f6;
    border-radius: 14px;
    padding: 14px 20px;
    margin-top: 14px;
  }
  .trainer-bottom-item {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 170px;
    flex: 1 1 170px;
  }
  .trainer-bottom-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #f3e8ff;
    color: #7c3aed;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
  }
  .trainer-bottom-label {
    font-size: 11px;
    color: #9ca3af;
  }
  .trainer-bottom-value {
    font-size: 13px;
    font-weight: 600;
    color: #111827;
  }
  .trainer-award-item {
    border: 1px solid #eef1f6;
    border-radius: 10px;
    padding: 14px 16px;
    margin-bottom: 12px;
    transition: box-shadow 0.15s, border-color 0.15s;
    display: flex;
    gap: 12px;
    align-items: flex-start;
  }
  .trainer-award-item:hover {
    box-shadow: 0 4px 14px rgba(0,0,0,0.08);
    border-color: #d6e4ff;
  }
  .trainer-award-icon {
    flex: 0 0 auto;
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: linear-gradient(135deg, #ffd666, #fa8c16);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 17px;
  }
  .trainer-award-body {
    flex: 1 1 auto;
    min-width: 0;
  }
  .trainer-media-section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    color: #1f2937;
    margin: 4px 0 10px;
  }
  .trainer-media-empty {
    color: #9ca3af;
    font-size: 13px;
    margin-bottom: 8px;
  }
  .trainer-link-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 20px;
    background: #f0f5ff;
    color: #1677ff !important;
    font-size: 12px;
    margin: 0 6px 6px 0;
  }
  .trainer-link-chip:hover {
    background: #d6e4ff;
  }
  .trainer-stat-row {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
    margin-bottom: 18px;
  }
  @media (max-width: 900px) {
    .trainer-stat-row { grid-template-columns: repeat(2, 1fr); }
  }
  .trainer-stat-card {
    background: #fff;
    border: 1px solid #eef1f6;
    border-radius: 15px;
    padding: 17px 18px;
    display: flex;
    align-items: center;
    gap: 12px;
    box-shadow: 0 5px 18px rgba(31, 45, 75, 0.06);
    transition: transform 0.15s, box-shadow 0.15s;
  }
  .trainer-stat-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(16, 24, 40, 0.08);
  }
  .trainer-stat-icon {
    width: 46px;
    height: 46px;
    border-radius: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 19px;
    color: #fff;
    flex: 0 0 auto;
    box-shadow: inset 0 1px 0 rgba(255,255,255,.28), 0 7px 14px rgba(15,23,42,.12);
  }
  .trainer-main-card.ant-card { border: 1px solid #e8edf5; border-radius: 14px !important; box-shadow: 0 5px 18px rgba(31,45,75,.05); }
  .trainer-main-card > .ant-card-body { padding: 16px; }
  .trainer-action-btn.ant-btn { width: 32px; height: 32px; border-radius: 8px; color: #52627a; }
  .trainer-action-btn.ant-btn:hover { color: #2563eb !important; background: #edf4ff !important; }
  @media (max-width: 700px) {
    .trainer-page-header { padding: 20px; align-items: flex-start; flex-direction: column; }
    .trainer-toolbar { align-items: stretch; flex-direction: column; }
    .trainer-filter-grid { grid-template-columns: 1fr 1fr; }
    .trainer-main-card > .ant-card-body { padding: 12px; }
    .trainer-summary-row { grid-template-columns: 1fr; gap: 10px; }
  }
  .trainer-table .ant-table-tbody > tr:nth-child(even) > td {
    background: #fff;
  }
  .trainer-person { display: flex; align-items: center; gap: 10px; min-width: 210px; }
  .trainer-person-copy { min-width: 0; }
  .trainer-person-name { display: block; color: #1f2937; font-size: 13px; font-weight: 650; line-height: 1.35; }
  .trainer-person-role { display: block; max-width: 260px; overflow: hidden; color: #8a95a6; font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
  .trainer-contact-main { color: #3f4b5f; font-size: 12px; }
  .trainer-contact-sub { display: block; max-width: 210px; overflow: hidden; color: #929cab; font-size: 10.5px; text-overflow: ellipsis; white-space: nowrap; text-transform: capitalize; }
  .trainer-skill-tag.ant-tag { margin: 0 4px 3px 0; padding: 0 7px; border-color: #dbe7fb; border-radius: 5px; color: #3565a8; background: #f3f7fd; font-size: 10.5px; line-height: 20px; }
  .trainer-mode-tag.ant-tag { margin: 0; border-radius: 5px; font-size: 10.5px; }
  .trainer-table .ant-pagination { margin: 15px 2px !important; }
  .trainer-avatar-ring {
    border: 2px solid #52c41a;
    padding: 1px;
  }
  .trainer-avatar-ring-off {
    border: 2px solid #e5e7eb;
    padding: 1px;
  }
  .trainer-section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    color: #1f2937;
    font-size: 13px;
    margin: 14px 0 8px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }
  .trainer-section-title:first-child {
    margin-top: 0;
  }
  .trainer-card-title {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .trainer-info-card {
    border: 1px solid #eef1f6;
    border-radius: 10px;
    padding: 14px 16px;
    margin-bottom: 12px;
    transition: box-shadow 0.15s, border-color 0.15s;
  }
  .trainer-info-card:hover {
    box-shadow: 0 4px 14px rgba(0,0,0,0.08);
    border-color: #d6e4ff;
  }
  .trainer-lang-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    border-radius: 10px;
    background: #f7f9fc;
    border: 1px solid #eef1f6;
    margin: 0 10px 10px 0;
  }
  .trainer-ant-tabs .ant-tabs-tab {
    padding: 10px 4px !important;
  }
`;

const TrainerForm: React.FC = () => {
  const [trainersData, setTrainersData] = useState<Trainer[]>([]);
  const [loading, setLoading] = useState(true);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);

  // Drawer state
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedTrainer, setSelectedTrainer] = useState<Trainer | null>(null);
  const [drawerTab, setDrawerTab] = useState("overview");

  // Pagination
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 12,
    total: 0,
  });

  // Filters
  const [searchText, setSearchText] = useState("");
  const [nameInput, setNameInput] = useState("");
  const [ratingMin, setRatingMin] = useState<number | undefined>(undefined);
  const [priceMin, setPriceMin] = useState<number | undefined>(undefined);
  const [priceMax, setPriceMax] = useState<number | undefined>(undefined);
  const [experience, setExperience] = useState<string | undefined>(undefined);
  const [skills, setSkills] = useState<string[]>([]);
  const [profileVerified, setProfileVerified] = useState<string | undefined>(undefined);
  const [mode, setMode] = useState<string | undefined>(undefined);
  const [sortKey, setSortKey] = useState<string | undefined>("rating");
  const [sortDir, setSortDir] = useState<string | undefined>("DESC");
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Overview stats (global, independent of table filters/pagination)
  const [statsLoading, setStatsLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, verified: 0, avgRating: 0, avgPrice: 0 });
  const [unverifiedCount, setUnverifiedCount] = useState(0);
  const [unverifiedList, setUnverifiedList] = useState<Trainer[]>([]);
  const [notifOpen, setNotifOpen] = useState(false);

  // Add Trainer modal
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addForm] = Form.useForm();

  const getAuthToken = () => {
    const storedDetails = localStorage.getItem("userDetails");
    return storedDetails ? JSON.parse(storedDetails)?.accessToken : null;
  };

  const fetchTrainers = async (page: number, pageSize: number) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("pageNumber", String(page - 1));
      params.set("size", String(pageSize));
      if (nameInput.trim()) params.set("name", nameInput.trim());
      if (ratingMin !== undefined) params.set("ratingMin", String(ratingMin));
      if (priceMin !== undefined) params.set("priceMin", String(priceMin));
      if (priceMax !== undefined) params.set("priceMax", String(priceMax));
      if (experience) params.set("experience", experience);
      if (skills.length) params.set("skills", skills.join(","));
      if (profileVerified) params.set("profileVerified", profileVerified);
      if (mode) params.set("mode", mode);
      if (sortKey) params.set("sortKey", sortKey);
      if (sortDir) params.set("sortDir", sortDir);

      const response = await apiFetch(`${LIST_TRAINER_URL}?${params.toString()}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();

      let trainers: Trainer[] = [];
      let total = 0;

      if (result.data) {
        trainers = Array.isArray(result.data) ? result.data : result.data.content || [];
        total = result.totalItems || result.totalElements || result.total || trainers.length;
      } else if (Array.isArray(result)) {
        trainers = result;
        total = result.length;
      }

      setTrainersData(trainers);
      setPagination((prev) => ({ ...prev, current: page, pageSize, total }));
    } catch (err: any) {
      message.error(err.message || "Failed to fetch trainers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrainers(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameInput, ratingMin, priceMin, priceMax, experience, skills, profileVerified, mode, sortKey, sortDir]);

  // Global stats (total/verified/avg rating/avg price) and the not-verified notification
  // list are fetched independently of the table's own filters/pagination so the numbers
  // reflect the whole dataset, not just the current page.
  const fetchOverview = async () => {
    setStatsLoading(true);
    try {
      // Fetch the full roster once and derive verified/not-verified counts from the
      // actual isProfileVerified field on each record, rather than relying on the
      // backend's profileVerified=true/false query filter — some backends only honour
      // the "true" case and silently ignore "false", which was hiding unverified
      // trainers from the notification bell even though the table showed them.
      const allRes = await apiFetch(`${LIST_TRAINER_URL}?pageNumber=0&size=1000`);
      const allJson = await allRes.json();

      const allTrainers: Trainer[] = Array.isArray(allJson?.data)
        ? allJson.data
        : allJson?.data?.content || (Array.isArray(allJson) ? allJson : []);
      const totalCount = allJson?.totalItems || allJson?.totalElements || allJson?.total || allTrainers.length;

      const verifiedTrainers = allTrainers.filter((t) => t.isProfileVerified === true);
      const notVerifiedTrainers = allTrainers.filter((t) => t.isProfileVerified !== true);

      const ratedTrainers = allTrainers.filter((t) => !!t.rating);
      const pricedTrainers = allTrainers.filter((t) => !!t.pricePerHour);

      setStats({
        total: totalCount,
        verified: verifiedTrainers.length,
        avgRating: ratedTrainers.length
          ? ratedTrainers.reduce((sum, t) => sum + (t.rating || 0), 0) / ratedTrainers.length
          : 0,
        avgPrice: pricedTrainers.length
          ? Math.round(pricedTrainers.reduce((sum, t) => sum + (t.pricePerHour || 0), 0) / pricedTrainers.length)
          : 0,
      });
      setUnverifiedCount(notVerifiedTrainers.length);
      setUnverifiedList(notVerifiedTrainers.slice(0, 6));
    } catch (err) {
      // Overview stats are supplementary; failures here shouldn't block the main table.
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetFilters = () => {
    setSearchText("");
    setNameInput("");
    setRatingMin(undefined);
    setPriceMin(undefined);
    setPriceMax(undefined);
    setExperience(undefined);
    setSkills([]);
    setProfileVerified(undefined);
    setMode(undefined);
    setSortKey("rating");
    setSortDir("DESC");
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleVerifyToggle = async (trainer: Trainer) => {
    const token = getAuthToken();
    if (!token) {
      message.error("No authentication token found. Please log in again.");
      return;
    }
    setVerifyingId(trainer._id);
    try {
      const response = await apiFetch(`${VERIFY_TRAINER_URL_BASE}/${trainer._id}/profile-verification`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ isProfileVerified: !trainer.isProfileVerified }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result?.message || "Failed to update verification status");

      message.success(result?.message || "Trainer profile verification updated");
      setTrainersData((prev) =>
        prev.map((t) => (t._id === trainer._id ? { ...t, isProfileVerified: !trainer.isProfileVerified } : t))
      );
      if (selectedTrainer?._id === trainer._id) {
        setSelectedTrainer((prev) => (prev ? { ...prev, isProfileVerified: !trainer.isProfileVerified } : prev));
      }
      fetchOverview();
    } catch (err: any) {
      message.error(err.message || "Failed to update verification status");
    } finally {
      setVerifyingId(null);
    }
  };

  const handleCreateTrainer = async (values: any) => {
    const token = getAuthToken();
    if (!token) {
      message.error("No authentication token found. Please log in again.");
      return;
    }
    setAddSubmitting(true);
    try {
      const payload = {
        ...values,
        skills: values.skills || [],
        industrySectorExpertise: values.industrySectorExpertise || [],
        domainDepartmentalExpertise: values.domainDepartmentalExpertise || [],
        competencyExpertise: values.competencyExpertise || [],
      };
      const response = await apiFetch(VERIFY_TRAINER_URL_BASE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result?.message || "Failed to add trainer");

      message.success(result?.message || "Trainer added successfully");
      setAddModalOpen(false);
      addForm.resetFields();
      fetchTrainers(1, pagination.pageSize);
      fetchOverview();
    } catch (err: any) {
      message.error(err.message || "Failed to add trainer");
    } finally {
      setAddSubmitting(false);
    }
  };

  const handleDeleteTrainer = async (trainerId: string) => {
    const token = getAuthToken();
    if (!token) {
      message.error("No authentication token found. Please log in again.");
      return;
    }
    try {
      const response = await apiFetch(`${VERIFY_TRAINER_URL_BASE}/${trainerId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result?.message || "Failed to delete trainer");

      message.success(result?.message || "Trainer deleted successfully");
      fetchTrainers(pagination.current, pagination.pageSize);
      fetchOverview();
    } catch (err: any) {
      message.error(err.message || "Failed to delete trainer");
    }
  };

  const modeColor = (v?: string) => {
    if (v === "Online") return "purple";
    if (v === "Offline" || v === "In-Person") return "blue";
    if (v === "Both") return "orange";
    return "default";
  };

  const columns = [
    {
      title: "S.No",
      key: "sno",
      width: 60,
      render: (_: any, __: any, index: number) => (
        <Text>{(pagination.current - 1) * pagination.pageSize + index + 1}</Text>
      ),
    },
    {
      title: "Trainer",
      key: "name",
      filteredValue: nameInput ? [nameInput] : null,
      filterDropdown: ({ confirm, clearFilters }: any) => (
        <div style={{ padding: 10, width: 240 }}>
          <Input
            autoFocus
            prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
            placeholder="Search trainer name"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            onPressEnter={() => {
              setNameInput(searchText.trim());
              confirm();
            }}
          />
          <Space style={{ marginTop: 10, width: "100%", justifyContent: "flex-end" }}>
            <Button
              size="small"
              onClick={() => {
                setSearchText("");
                setNameInput("");
                clearFilters?.();
                confirm();
              }}
            >
              Reset
            </Button>
            <Button size="small" type="primary" onClick={() => { setNameInput(searchText.trim()); confirm(); }}>
              Search
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#2563eb" : undefined }} />,
      render: (_: any, record: Trainer) => (
        <div className="trainer-person">
          <Avatar
            className={record.isProfileVerified ? "trainer-avatar-ring" : "trainer-avatar-ring-off"}
            icon={<UserOutlined />}
            src={record.profilePhoto}
            size={36}
          />
          <div className="trainer-person-copy">
            <span className="trainer-person-name">{record.name || "Unnamed trainer"}</span>
            <span className="trainer-person-role">{record.subjectLine || "No specialization added"}</span>
          </div>
        </div>
      ),
    },
    {
      title: "Contact",
      key: "contact",
      render: (_: any, record: Trainer) => (
        <div>
          <span className="trainer-contact-main">{record.mobile || "Not provided"}</span>
          <span className="trainer-contact-sub">
            {[record.city, record.state, record.country].filter(Boolean).join(", ") || "Location not provided"}
          </span>
        </div>
      ),
    },
    {
      title: "Skills",
      key: "skills",
      render: (_: any, record: Trainer) => (
        <Space size={[0, 4]} wrap style={{ maxWidth: 220 }}>
          {(record.skills || []).slice(0, 3).map((s) => (
            <Tag className="trainer-skill-tag" key={s}>
              {s}
            </Tag>
          ))}
          {(record.skills?.length || 0) > 3 && <Tag className="trainer-skill-tag">+{(record.skills!.length - 3)}</Tag>}
          {!record.skills?.length && <Text type="secondary" style={{ fontSize: 12 }}>No skills</Text>}
        </Space>
      ),
    },
    {
      title: "Experience",
      dataIndex: "experienceYears",
      width: 100,
      filters: [
        { text: "1+ years", value: "1+" },
        { text: "3+ years", value: "3+" },
        { text: "5+ years", value: "5+" },
        { text: "10+ years", value: "10+" },
      ],
      filteredValue: experience ? [experience] : null,
      filterMultiple: false,
      sorter: true,
      render: (v: number) => (v ? `${v} yrs` : "N/A"),
    },
    {
      title: "Price/Hr",
      dataIndex: "pricePerHour",
      width: 100,
      sorter: true,
      render: (v: number) => (v ? `₹${v}` : "N/A"),
    },
    {
      title: "Rating",
      dataIndex: "rating",
      width: 100,
      sorter: true,
      render: (v: number) =>
        v ? (
          <Space size={4}>
            <StarFilled style={{ color: "#faad14" }} />
            <Text strong>{v.toFixed(1)}</Text>
          </Space>
        ) : (
          <Text type="secondary">N/A</Text>
        ),
    },
    {
      title: "Mode",
      dataIndex: "mode",
      width: 90,
      filters: ["Online", "Offline", "In-Person", "Both"].map((value) => ({ text: value, value })),
      filteredValue: mode ? [mode] : null,
      filterMultiple: false,
      render: (v: string) => (v ? <Tag className="trainer-mode-tag" color={modeColor(v)}>{v}</Tag> : <Text type="secondary">N/A</Text>),
    },
    {
      title: "Profile Verified",
      key: "verified",
      width: 150,
      filters: [
        { text: "Verified", value: "true" },
        { text: "Not Verified", value: "false" },
      ],
      filteredValue: profileVerified ? [profileVerified] : null,
      filterMultiple: false,
      render: (_: any, record: Trainer) => (
        <Popconfirm
          title={`Mark profile as ${record.isProfileVerified ? "unverified" : "verified"}?`}
          onConfirm={() => handleVerifyToggle(record)}
          okText="Yes"
          cancelText="No"
        >
          <Tag
            className="trainer-verified-pill"
            color={record.isProfileVerified ? "green" : "default"}
            icon={record.isProfileVerified ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
            style={{ cursor: "pointer" }}
          >
            {record.isProfileVerified ? "Verified" : "Not Verified"}
            {verifyingId === record._id && "..."}
          </Tag>
        </Popconfirm>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      fixed: "right" as const,
      render: (_: any, record: Trainer) => (
        <Space size={4}>
          <Button
            className="trainer-action-btn"
            type="text"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedTrainer(record);
              setDrawerTab("overview");
              setDrawerVisible(true);
            }}
          />
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: "view",
                  label: "View Profile",
                  icon: <EyeOutlined />,
                  onClick: () => {
                    setSelectedTrainer(record);
                    setDrawerTab("overview");
                    setDrawerVisible(true);
                  },
                },
                {
                  key: "verify",
                  label: record.isProfileVerified ? "Mark Not Verified" : "Mark Verified",
                  icon: record.isProfileVerified ? <CloseCircleOutlined /> : <CheckCircleOutlined />,
                  onClick: () => handleVerifyToggle(record),
                },
                {
                  key: "delete",
                  label: "Delete Trainer",
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: () => {
                    Modal.confirm({
                      title: "Are you sure you want to delete this trainer?",
                      content: "This action cannot be undone.",
                      okText: "Yes, Delete",
                      okType: "danger",
                      cancelText: "Cancel",
                      onOk: () => handleDeleteTrainer(record._id),
                    });
                  },
                },
              ],
            }}
          >
            <Button className="trainer-action-btn" type="text" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      ),
    },
  ];

  return (
    <div className="trainer-page">
      <style>{TRAINER_STYLES}</style>
      <div className="trainer-simple-heading">
        <div>
          <div className="trainer-title-row">
            <h1>Trainer Management</h1>
          </div>
          <p>Manage and review all trainer profiles</p>
        </div>
        <Button icon={<ReloadOutlined />} onClick={resetFilters}>Clear filters</Button>
      </div>
      <div className="trainer-summary-row">
        <div className="trainer-summary-card">
          <div className="trainer-summary-icon" style={{ background: "linear-gradient(135deg, #3b82f6, #1d4ed8)" }}>
            <TeamOutlined />
          </div>
          <div>
            <div className="trainer-summary-label">Total Profiles</div>
            <div className="trainer-summary-value">{statsLoading ? "--" : stats.total}</div>
            <div className="trainer-summary-caption">All registered trainers</div>
          </div>
        </div>
        <div className="trainer-summary-card">
          <div className="trainer-summary-icon" style={{ background: "linear-gradient(135deg, #34d399, #059669)" }}>
            <CheckCircleOutlined />
          </div>
          <div>
            <div className="trainer-summary-label">Verified Profiles</div>
            <div className="trainer-summary-value">{statsLoading ? "--" : stats.verified}</div>
            <div className="trainer-summary-caption">Approved trainer profiles</div>
          </div>
        </div>
      </div>

      <Card bordered={false} className="trainer-main-card">
        {/* Filters now live in the relevant table-column headers. */}
        {false && <div className="trainer-filter-bar"><div className="trainer-filter-grid">
          <div className="trainer-filter-field">
            <label>Min Rating</label>
            <InputNumber
              placeholder="Select"
              min={0}
              max={5}
              style={{ width: "100%" }}
              value={ratingMin}
              onChange={(v) => setRatingMin(v ?? undefined)}
            />
          </div>
          <div className="trainer-filter-field">
            <label>Min Price</label>
            <InputNumber
              placeholder="Select"
              min={0}
              style={{ width: "100%" }}
              value={priceMin}
              onChange={(v) => setPriceMin(v ?? undefined)}
            />
          </div>
          <div className="trainer-filter-field">
            <label>Max Price</label>
            <InputNumber
              placeholder="Select"
              min={0}
              style={{ width: "100%" }}
              value={priceMax}
              onChange={(v) => setPriceMax(v ?? undefined)}
            />
          </div>
          <div className="trainer-filter-field">
            <label>Experience</label>
            <Select placeholder="Select" allowClear style={{ width: "100%" }} value={experience} onChange={setExperience}>
              <Option value="1+">1+ years</Option>
              <Option value="3+">3+ years</Option>
              <Option value="5+">5+ years</Option>
              <Option value="10+">10+ years</Option>
            </Select>
          </div>
          <div className="trainer-filter-field">
            <label>Skills</label>
            <Select
              mode="tags"
              placeholder="Select"
              allowClear
              style={{ width: "100%" }}
              value={skills}
              onChange={setSkills}
              tokenSeparators={[","]}
            />
          </div>
          <div className="trainer-filter-field">
            <label>Verified</label>
            <Select placeholder="All" allowClear style={{ width: "100%" }} value={profileVerified} onChange={setProfileVerified}>
              <Option value="true">Verified</Option>
              <Option value="false">Not Verified</Option>
            </Select>
          </div>
          <div className="trainer-filter-field">
            <label>Mode</label>
            <Select placeholder="All" allowClear style={{ width: "100%" }} value={mode} onChange={setMode}>
              <Option value="Online">Online</Option>
              <Option value="Offline">Offline</Option>
              <Option value="In-Person">In-Person</Option>
              <Option value="Both">Both</Option>
            </Select>
          </div>
          <div className="trainer-filter-field">
            <label>Sort By</label>
            <Select
              style={{ width: "100%" }}
              value={sortKey ? `${sortKey}_${sortDir}` : undefined}
              onChange={(v) => {
                const [key, dir] = v.split("_");
                setSortKey(key);
                setSortDir(dir);
              }}
            >
              <Option value="rating_DESC">Rating (High - Low)</Option>
              <Option value="rating_ASC">Rating (Low - High)</Option>
              <Option value="price_ASC">Price (Low - High)</Option>
              <Option value="price_DESC">Price (High - Low)</Option>
              <Option value="experience_DESC">Experience (High - Low)</Option>
              <Option value="experience_ASC">Experience (Low - High)</Option>
            </Select>
          </div>
          <div className="trainer-filter-actions">
            <Button icon={<ReloadOutlined />} onClick={resetFilters}>
              Reset
            </Button>
            {/* <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalOpen(true)}>
                Add Trainer
              </Button> */}
          </div>
        </div></div>}

        <Table
          className="trainer-table"
          columns={columns as any}
          dataSource={trainersData}
          rowKey="_id"
          loading={loading}
          scroll={{ x: "max-content" }}
          onChange={(tablePagination, tableFilters: any, tableSorter: any) => {
            const page = tablePagination.current || 1;
            const pageSize = tablePagination.pageSize || pagination.pageSize;
            const nextExperience = tableFilters.experienceYears?.[0];
            const nextMode = tableFilters.mode?.[0];
            const nextVerified = tableFilters.verified?.[0];
            let nextSortKey: string | undefined;
            let nextSortDir: string | undefined;
            if (tableSorter?.field && tableSorter?.order) {
              const sortMap: Record<string, string> = { rating: "rating", pricePerHour: "price", experienceYears: "experience" };
              nextSortKey = sortMap[tableSorter.field] || tableSorter.field;
              nextSortDir = tableSorter.order === "ascend" ? "ASC" : "DESC";
            }
            const queryChanged = nextExperience !== experience || nextMode !== mode ||
              nextVerified !== profileVerified || nextSortKey !== sortKey || nextSortDir !== sortDir;
            setExperience(nextExperience);
            setMode(nextMode);
            setProfileVerified(nextVerified);
            setSortKey(nextSortKey);
            setSortDir(nextSortDir);
            if (queryChanged) {
              setPagination((prev) => ({ ...prev, current: 1, pageSize }));
            } else {
              fetchTrainers(page, pageSize);
            }
          }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ["12", "24", "48", "100"],
            showTotal: (total, range) => `Showing ${range[0]} to ${range[1]} of ${total} trainers`,
            position: ["topRight", "bottomRight"],
          }}
        />
      </Card>

      <Drawer
        title={<Text strong>Trainer Profile</Text>}
        width="85%"
        onClose={() => {
          setDrawerVisible(false);
          setDrawerTab("overview");
        }}
        visible={drawerVisible}
        destroyOnClose
        bodyStyle={{ padding: "0 20px 20px", background: "#fafbfc" }}
      >
        {selectedTrainer && (
          <Tabs activeKey={drawerTab} onChange={setDrawerTab} size="large" className="trainer-ant-tabs">
            <TabPane tab={<span><InfoCircleOutlined /> Overview</span>} key="overview">
              {(() => {
                const workshopsCount = selectedTrainer.workshops?.length || 0;
                const totalParticipants = (selectedTrainer.workshops || []).reduce(
                  (sum, w) => sum + (w.totalParticipants || 0),
                  0
                );
                const awardsCount = selectedTrainer.awards?.length || 0;
                const latestExperience = selectedTrainer.experience?.[0];
                const latestWorkshop = selectedTrainer.workshops?.[0];
                const latestAward = selectedTrainer.awards?.[0];

                return (
                  <div style={{ marginTop: 14 }}>
                    {/* Profile card */}
                    <div className="trainer-profile-card">
                      <div className="trainer-profile-left">
                        <div className="trainer-profile-top">
                          <Avatar
                            className={selectedTrainer.isProfileVerified ? "trainer-avatar-ring" : "trainer-avatar-ring-off"}
                            size={88}
                            icon={<UserOutlined />}
                            src={selectedTrainer.profilePhoto}
                          />
                          <div>
                            <div className="trainer-name-row">
                              <Title level={4} style={{ margin: 0 }}>{selectedTrainer.name}</Title>
                              {selectedTrainer.isProfileVerified && (
                                <CheckCircleOutlined className="trainer-verified-badge" title="Profile Verified" />
                              )}
                            </div>
                            {selectedTrainer.subjectLine && (
                              <Text style={{ color: "#1677ff", fontWeight: 500, fontSize: 13.5 }}>{selectedTrainer.subjectLine}</Text>
                            )}
                            {selectedTrainer.tagLine && (
                              <div><Text type="secondary" style={{ fontSize: 13 }}>{selectedTrainer.tagLine}</Text></div>
                            )}

                            <div className="trainer-meta-row">
                              {(selectedTrainer.city || selectedTrainer.state || selectedTrainer.country) && (
                                <span><EnvironmentOutlined />{[selectedTrainer.city, selectedTrainer.state, selectedTrainer.country].filter(Boolean).join(", ")}</span>
                              )}
                              {selectedTrainer.mobile && <span><PhoneOutlined />{selectedTrainer.mobile}</span>}
                              {selectedTrainer.website && (
                                <Link href={selectedTrainer.website} target="_blank" style={{ color: "#6b7280" }}>
                                  <GlobalOutlined /> {selectedTrainer.website.replace(/^https?:\/\//, "")}
                                </Link>
                              )}
                            </div>

                            {(selectedTrainer.linkedinProfile || selectedTrainer.youtubeChannel || selectedTrainer.instagramHandle || selectedTrainer.facebookPage || selectedTrainer.website) && (
                              <div className="trainer-social-row">
                                {selectedTrainer.linkedinProfile && (
                                  <Link className="trainer-social-btn" href={selectedTrainer.linkedinProfile} target="_blank"><LinkedinOutlined /></Link>
                                )}
                                {selectedTrainer.youtubeChannel && (
                                  <Link className="trainer-social-btn" href={selectedTrainer.youtubeChannel} target="_blank"><YoutubeOutlined /></Link>
                                )}
                                {selectedTrainer.instagramHandle && (
                                  <span className="trainer-social-btn"><InstagramOutlined /></span>
                                )}
                                {selectedTrainer.facebookPage && (
                                  <Link className="trainer-social-btn" href={selectedTrainer.facebookPage} target="_blank"><FacebookOutlined /></Link>
                                )}
                                {selectedTrainer.website && (
                                  <Link className="trainer-social-btn" href={selectedTrainer.website} target="_blank"><GlobalOutlined /></Link>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="trainer-profile-banner">
                        {selectedTrainer.bannerImage && <img src={selectedTrainer.bannerImage} alt="" />}
                      </div>
                    </div>

                    {/* Quick facts bar */}
                    <div className="trainer-quickfacts-bar">
                      <Space size={32} wrap>
                        {selectedTrainer.trainerType && (
                          <div>
                            <div className="trainer-quickfact-label">Trainer Type</div>
                            <div className="trainer-quickfact-value">{selectedTrainer.trainerType}</div>
                          </div>
                        )}
                        {!!selectedTrainer.experienceYears && (
                          <div>
                            <div className="trainer-quickfact-label">Experience</div>
                            <div className="trainer-quickfact-value">{selectedTrainer.experienceYears}+ Years</div>
                          </div>
                        )}
                        {selectedTrainer.mode && (
                          <div>
                            <div className="trainer-quickfact-label">Mode</div>
                            <div className="trainer-quickfact-value">{selectedTrainer.mode}</div>
                          </div>
                        )}
                        {!!selectedTrainer.pricePerHour && (
                          <div>
                            <div className="trainer-quickfact-label">Price / Hour</div>
                            <div className="trainer-quickfact-value">₹{selectedTrainer.pricePerHour}</div>
                          </div>
                        )}
                        <div>
                          <div className="trainer-quickfact-label">Available This Week</div>
                          <Tag color={selectedTrainer.availableThisWeek ? "green" : "default"} style={{ margin: 0 }}>
                            {selectedTrainer.availableThisWeek ? "Yes" : "No"}
                          </Tag>
                        </div>
                        <div>
                          <div className="trainer-quickfact-label">Trial Sessions</div>
                          <Tag color={selectedTrainer.offersTrialSessions ? "green" : "default"} style={{ margin: 0 }}>
                            {selectedTrainer.offersTrialSessions ? "Yes" : "No"}
                          </Tag>
                        </div>
                      </Space>

                      <Popconfirm
                        title={`Mark profile as ${selectedTrainer.isProfileVerified ? "unverified" : "verified"}?`}
                        onConfirm={() => handleVerifyToggle(selectedTrainer)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button
                          icon={selectedTrainer.isProfileVerified ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                          type={selectedTrainer.isProfileVerified ? "default" : "primary"}
                          loading={verifyingId === selectedTrainer._id}
                        >
                          {selectedTrainer.isProfileVerified ? "Verified" : "Verify Profile"}
                        </Button>
                      </Popconfirm>
                    </div>

                    {/* Stats row */}
                    <div className="trainer-stats-grid">
                      <div className="trainer-stat-card">
                        <div className="trainer-stat-icon" style={{ background: "linear-gradient(135deg, #7dd3fc, #0ea5e9)" }}>
                          <ScheduleOutlined />
                        </div>
                        <Statistic title="Workshops Conducted" value={workshopsCount} />
                      </div>
                      <div className="trainer-stat-card">
                        <div className="trainer-stat-icon" style={{ background: "linear-gradient(135deg, #93c5fd, #2563eb)" }}>
                          <TeamOutlined />
                        </div>
                        <Statistic title="Total Participants" value={totalParticipants} />
                      </div>
                      <div className="trainer-stat-card">
                        <div className="trainer-stat-icon" style={{ background: "linear-gradient(135deg, #ffd666, #fa8c16)" }}>
                          <StarFilled />
                        </div>
                        <Statistic title="Average Rating" value={selectedTrainer.rating || 0} precision={1} />
                      </div>
                      <div className="trainer-stat-card">
                        <div className="trainer-stat-icon" style={{ background: "linear-gradient(135deg, #f9a8d4, #db2777)" }}>
                          <BankOutlined />
                        </div>
                        <Statistic title="Experience" value={selectedTrainer.experienceYears || 0} suffix="yrs" />
                      </div>
                      <div className="trainer-stat-card">
                        <div className="trainer-stat-icon" style={{ background: "linear-gradient(135deg, #d8b4fe, #7c3aed)" }}>
                          <TrophyOutlined />
                        </div>
                        <Statistic title="Awards Won" value={awardsCount} />
                      </div>
                    </div>

                    {/* Row 1: About / Expertise / Experience */}
                    <div className="trainer-grid-3">
                      <div className="trainer-panel">
                        <div className="trainer-panel-header">
                          <span className="trainer-panel-title"><UserOutlined style={{ color: "#1677ff" }} /> About Me</span>
                        </div>
                        <Paragraph type="secondary" style={{ fontSize: 12.5, marginBottom: 0 }} ellipsis={{ rows: 5 }}>
                          {selectedTrainer.profileSummary || "No summary provided."}
                        </Paragraph>
                      </div>

                      <div className="trainer-panel">
                        <div className="trainer-panel-header">
                          <span className="trainer-panel-title"><BulbOutlined style={{ color: "#7c3aed" }} /> Industry & Expertise</span>
                          <span className="trainer-panel-viewall" onClick={() => setDrawerTab("expertise")}>View All</span>
                        </div>
                        {selectedTrainer.industrySectorExpertise?.length ? (
                          <>
                            <Text style={{ fontSize: 12, fontWeight: 600 }}>Industry Expertise</Text>
                            <div style={{ margin: "4px 0 8px" }}>
                              <Space size={[6, 6]} wrap>
                                {selectedTrainer.industrySectorExpertise.slice(0, 4).map((s) => <Tag key={s}>{s}</Tag>)}
                              </Space>
                            </div>
                          </>
                        ) : null}
                        {selectedTrainer.domainDepartmentalExpertise?.length ? (
                          <>
                            <Text style={{ fontSize: 12, fontWeight: 600 }}>Domain Expertise</Text>
                            <div style={{ margin: "4px 0 8px" }}>
                              <Space size={[6, 6]} wrap>
                                {selectedTrainer.domainDepartmentalExpertise.slice(0, 4).map((s) => <Tag key={s}>{s}</Tag>)}
                              </Space>
                            </div>
                          </>
                        ) : null}
                        {selectedTrainer.competencyExpertise?.length ? (
                          <>
                            <Text style={{ fontSize: 12, fontWeight: 600 }}>Competency Expertise</Text>
                            <div style={{ margin: "4px 0 0" }}>
                              <Space size={[6, 6]} wrap>
                                {selectedTrainer.competencyExpertise.slice(0, 4).map((s) => <Tag key={s}>{s}</Tag>)}
                              </Space>
                            </div>
                          </>
                        ) : null}
                        {!selectedTrainer.industrySectorExpertise?.length && !selectedTrainer.domainDepartmentalExpertise?.length && !selectedTrainer.competencyExpertise?.length && (
                          <div className="trainer-panel-empty">No expertise data.</div>
                        )}
                      </div>

                      <div className="trainer-panel">
                        <div className="trainer-panel-header">
                          <span className="trainer-panel-title"><BankOutlined style={{ color: "#1677ff" }} /> Experience Summary</span>
                          <span className="trainer-panel-viewall" onClick={() => setDrawerTab("exp")}>View All</span>
                        </div>
                        {latestExperience ? (
                          <div>
                            <Space align="start" style={{ width: "100%", justifyContent: "space-between" }}>
                              <Text strong style={{ fontSize: 13 }}>{latestExperience.jobTitle}</Text>
                              <Tag color="blue" style={{ margin: 0 }}>{latestExperience.startYear} - {latestExperience.endYear || "Present"}</Tag>
                            </Space>
                            <div><Text type="secondary" style={{ fontSize: 12 }}>{latestExperience.organisation}</Text></div>
                            {latestExperience.achievements && (
                              <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 6, marginBottom: 0 }} ellipsis={{ rows: 2 }}>
                                {latestExperience.achievements}
                              </Paragraph>
                            )}
                          </div>
                        ) : <div className="trainer-panel-empty">No experience data.</div>}
                      </div>
                    </div>

                    {/* Row 2: Workshop / Awards / Media */}
                    <div className="trainer-grid-3">
                      <div className="trainer-panel">
                        <div className="trainer-panel-header">
                          <span className="trainer-panel-title"><ScheduleOutlined style={{ color: "#0ea5e9" }} /> Recent Workshop</span>
                          <span className="trainer-panel-viewall" onClick={() => setDrawerTab("workshops")}>View All</span>
                        </div>
                        {latestWorkshop ? (
                          <div>
                            {latestWorkshop.photos?.[0] && (
                              <Image
                                src={latestWorkshop.photos[0]}
                                width="100%"
                                height={110}
                                style={{ objectFit: "cover", borderRadius: 8, marginBottom: 8 }}
                              />
                            )}
                            <Text strong style={{ fontSize: 13 }}>{latestWorkshop.title}</Text>
                            <div><Text type="secondary" style={{ fontSize: 12 }}>{latestWorkshop.companyName}</Text></div>
                            <div style={{ marginTop: 6 }}>
                              <Space size={[6, 6]} wrap>
                                {latestWorkshop.duration && <Tag icon={<ScheduleOutlined />} style={{ margin: 0 }}>{latestWorkshop.duration}</Tag>}
                                {latestWorkshop.location && <Tag icon={<EnvironmentOutlined />} style={{ margin: 0 }}>{latestWorkshop.location}</Tag>}
                              </Space>
                              {!!latestWorkshop.totalParticipants && (
                                <Tag color="blue" style={{ marginLeft: 6 }}>{latestWorkshop.totalParticipants} Participants</Tag>
                              )}
                            </div>
                          </div>
                        ) : <div className="trainer-panel-empty">No workshop data.</div>}
                      </div>

                      <div className="trainer-panel">
                        <div className="trainer-panel-header">
                          <span className="trainer-panel-title"><TrophyOutlined style={{ color: "#fa8c16" }} /> Achievements & Awards</span>
                          <span className="trainer-panel-viewall" onClick={() => setDrawerTab("media")}>View All</span>
                        </div>
                        {latestAward ? (
                          <div className="trainer-award-item" style={{ margin: 0, border: "none", padding: 0 }}>
                            <div className="trainer-award-icon"><TrophyOutlined /></div>
                            <div className="trainer-award-body">
                              <Text strong style={{ fontSize: 13 }}>{latestAward.title}</Text>
                              <div style={{ margin: "4px 0" }}>
                                <Space size={[6, 6]} wrap>
                                  {latestAward.organisation && <Tag color="blue" style={{ margin: 0 }}>{latestAward.organisation}</Tag>}
                                  {latestAward.year && <Tag color="gold" style={{ margin: 0 }}>{latestAward.year}</Tag>}
                                </Space>
                              </div>
                              {latestAward.description && (
                                <Paragraph type="secondary" style={{ fontSize: 12, margin: 0 }} ellipsis={{ rows: 2 }}>
                                  {latestAward.description}
                                </Paragraph>
                              )}
                            </div>
                          </div>
                        ) : <div className="trainer-panel-empty">No awards data.</div>}
                      </div>

                      <div className="trainer-panel">
                        <div className="trainer-panel-header">
                          <span className="trainer-panel-title"><PictureOutlined style={{ color: "#722ed1" }} /> Documents & Media</span>
                          <span className="trainer-panel-viewall" onClick={() => setDrawerTab("media")}>View All</span>
                        </div>
                        <div className="trainer-doc-grid">
                          <a
                            className={`trainer-doc-tile${selectedTrainer.profileDocument ? "" : " disabled"}`}
                            href={selectedTrainer.profileDocument || undefined}
                            target={selectedTrainer.profileDocument ? "_blank" : undefined}
                            rel="noreferrer"
                            onClick={(e) => { if (!selectedTrainer.profileDocument) e.preventDefault(); }}
                          >
                            <div className="trainer-doc-icon" style={{ background: "#f5222d" }}><FileTextOutlined /></div>
                            <div className="trainer-doc-label">Profile Document</div>
                          </a>
                          <div className="trainer-doc-tile" onClick={() => setDrawerTab("media")}>
                            {!!selectedTrainer.certificates?.length && (
                              <span className="trainer-doc-badge">{selectedTrainer.certificates.length}</span>
                            )}
                            <div className="trainer-doc-icon" style={{ background: "#fa8c16" }}><SafetyCertificateOutlined /></div>
                            <div className="trainer-doc-label">Certificates</div>
                          </div>
                          <div className="trainer-doc-tile" onClick={() => setDrawerTab("media")}>
                            {!!selectedTrainer.galleryPhotos?.length && (
                              <span className="trainer-doc-badge">{selectedTrainer.galleryPhotos.length}</span>
                            )}
                            <div className="trainer-doc-icon" style={{ background: "#52c41a" }}><PictureOutlined /></div>
                            <div className="trainer-doc-label">Gallery</div>
                          </div>
                          <div className="trainer-doc-tile" onClick={() => setDrawerTab("media")}>
                            {!!selectedTrainer.videoUrls?.length && (
                              <span className="trainer-doc-badge">{selectedTrainer.videoUrls.length}</span>
                            )}
                            <div className="trainer-doc-icon" style={{ background: "#1677ff" }}><VideoCameraOutlined /></div>
                            <div className="trainer-doc-label">Videos</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Bottom bar */}
                    <div className="trainer-bottom-bar">
                      {!!selectedTrainer.languagesFluent?.length && (
                        <div className="trainer-bottom-item">
                          <div className="trainer-bottom-icon"><TranslationOutlined /></div>
                          <div>
                            <div className="trainer-bottom-label">Languages</div>
                            <div className="trainer-bottom-value">
                              {selectedTrainer.languagesFluent.map((l) => `${l.language} (${l.proficiencyLevel})`).join(", ")}
                            </div>
                          </div>
                        </div>
                      )}
                      {selectedTrainer.openToTravel && (
                        <div className="trainer-bottom-item">
                          <div className="trainer-bottom-icon"><EnvironmentOutlined /></div>
                          <div>
                            <div className="trainer-bottom-label">Open to Travel</div>
                            <div className="trainer-bottom-value">{selectedTrainer.openToTravel}</div>
                          </div>
                        </div>
                      )}
                      {selectedTrainer.trainingExperienceLevel && (
                        <div className="trainer-bottom-item">
                          <div className="trainer-bottom-icon"><StarFilled /></div>
                          <div>
                            <div className="trainer-bottom-label">Training Experience</div>
                            <div className="trainer-bottom-value">{selectedTrainer.trainingExperienceLevel}</div>
                          </div>
                        </div>
                      )}
                      {selectedTrainer.audienceSeniorityLevel && (
                        <div className="trainer-bottom-item">
                          <div className="trainer-bottom-icon"><TeamOutlined /></div>
                          <div>
                            <div className="trainer-bottom-label">Audience Level</div>
                            <div className="trainer-bottom-value">{selectedTrainer.audienceSeniorityLevel}</div>
                          </div>
                        </div>
                      )}
                      {selectedTrainer.commercialsFeeRange && (
                        <div className="trainer-bottom-item">
                          <div className="trainer-bottom-icon"><RupeeIcon style={{ fontSize: 15 }} /></div>
                          <div>
                            <div className="trainer-bottom-label">Commercials</div>
                            <div className="trainer-bottom-value">{selectedTrainer.commercialsFeeRange}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </TabPane>

            <TabPane tab={<span><BulbOutlined /> Expertise & Commercials</span>} key="expertise">
              <Row gutter={[16, 16]} style={{ marginTop: 14 }}>
                <Col span={12}>
                  <Card
                    title={<span className="trainer-card-title"><BulbOutlined style={{ color: "#1677ff" }} /> Expertise</span>}
                    bordered={false}
                    className="shadow-sm"
                  >
                    <div className="trainer-section-title">Industry / Sector</div>
                    <Space size={[0, 8]} wrap>
                      {selectedTrainer.industrySectorExpertise?.length
                        ? selectedTrainer.industrySectorExpertise.map((s) => <Tag color="blue" key={s}>{s}</Tag>)
                        : <Text type="secondary" style={{ fontSize: 12 }}>N/A</Text>}
                    </Space>
                    <div className="trainer-section-title">Domain / Department</div>
                    <Space size={[0, 8]} wrap>
                      {selectedTrainer.domainDepartmentalExpertise?.length
                        ? selectedTrainer.domainDepartmentalExpertise.map((s) => <Tag color="cyan" key={s}>{s}</Tag>)
                        : <Text type="secondary" style={{ fontSize: 12 }}>N/A</Text>}
                    </Space>
                    <div className="trainer-section-title">Competency</div>
                    <Space size={[0, 8]} wrap>
                      {selectedTrainer.competencyExpertise?.length
                        ? selectedTrainer.competencyExpertise.map((s) => <Tag color="purple" key={s}>{s}</Tag>)
                        : <Text type="secondary" style={{ fontSize: 12 }}>N/A</Text>}
                    </Space>
                    <div className="trainer-section-title">Skills</div>
                    <Space size={[0, 8]} wrap>
                      {selectedTrainer.skills?.length
                        ? selectedTrainer.skills.map((s) => <Tag color="geekblue" key={s}>{s}</Tag>)
                        : <Text type="secondary" style={{ fontSize: 12 }}>N/A</Text>}
                    </Space>
                    <div className="trainer-section-title">Certifications</div>
                    <Space size={[0, 8]} wrap>
                      {selectedTrainer.trainerCertifications?.length
                        ? selectedTrainer.trainerCertifications.map((s) => <Tag color="gold" icon={<SafetyCertificateOutlined />} key={s}>{s}</Tag>)
                        : <Text type="secondary" style={{ fontSize: 12 }}>N/A</Text>}
                    </Space>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card
                    title={<span className="trainer-card-title"><RupeeIcon style={{ color: "#722ed1", fontSize: 15 }} /> Commercials</span>}
                    bordered={false}
                    className="shadow-sm"
                  >
                    <Row gutter={[12, 12]}>
                      <Col span={12}>
                        <div className="trainer-info-card" style={{ textAlign: "center" }}>
                          <Statistic
                            title="Price / Hour"
                            value={selectedTrainer.pricePerHour || 0}
                            prefix="₹"
                            valueStyle={{ fontSize: 20, color: "#1677ff" }}
                          />
                        </div>
                      </Col>
                      <Col span={12}>
                        <div className="trainer-info-card" style={{ textAlign: "center" }}>
                          <Statistic
                            title="Experience"
                            value={selectedTrainer.experienceYears || 0}
                            suffix="yrs"
                            valueStyle={{ fontSize: 20, color: "#1677ff" }}
                          />
                        </div>
                      </Col>
                    </Row>
                    <Descriptions column={1} size="small" bordered style={{ marginTop: 4 }}>
                      <Descriptions.Item label="Fee Range">{selectedTrainer.commercialsFeeRange || "N/A"}</Descriptions.Item>
                      <Descriptions.Item label="Training Experience Level">{selectedTrainer.trainingExperienceLevel || "N/A"}</Descriptions.Item>
                      <Descriptions.Item label="Audience Seniority Level">{selectedTrainer.audienceSeniorityLevel || "N/A"}</Descriptions.Item>
                      <Descriptions.Item label="Rating">
                        <Space>
                          <Rate disabled allowHalf defaultValue={selectedTrainer.rating || 0} />
                          <Text type="secondary">({(selectedTrainer.rating || 0).toFixed(1)})</Text>
                        </Space>
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
              </Row>
            </TabPane>

            <TabPane tab={<span><BankOutlined /> Experience & Education</span>} key="exp">
              <Row gutter={[16, 16]} style={{ marginTop: 14 }}>
                <Col span={12}>
                  <Card
                    title={<span className="trainer-card-title"><BankOutlined style={{ color: "#1677ff" }} /> Professional Experience</span>}
                    bordered={false}
                    className="shadow-sm"
                  >
                    {selectedTrainer.experience?.length ? (
                      <Timeline style={{ marginTop: 8 }}>
                        {selectedTrainer.experience.map((item, i) => (
                          <Timeline.Item key={i} color="#1677ff">
                            <Text strong>{item.jobTitle}</Text>
                            <Text type="secondary"> @ {item.organisation}</Text>
                            <div style={{ margin: "4px 0 6px" }}>
                              <Space size={[6, 6]} wrap>
                                <Tag color="blue">{item.startYear} - {item.endYear || "Present"}</Tag>
                                {item.employmentType && <Tag color="geekblue">{item.employmentType}</Tag>}
                                {item.industry && <Tag color="cyan">{item.industry}</Tag>}
                              </Space>
                            </div>
                            {item.achievements && (
                              <Paragraph type="secondary" style={{ fontSize: 13, margin: 0 }}>{item.achievements}</Paragraph>
                            )}
                          </Timeline.Item>
                        ))}
                      </Timeline>
                    ) : <Text type="secondary">No experience data.</Text>}
                  </Card>
                </Col>
                <Col span={12}>
                  <Card
                    title={<span className="trainer-card-title"><ReadOutlined style={{ color: "#13a8a8" }} /> Education</span>}
                    bordered={false}
                    className="shadow-sm"
                  >
                    {selectedTrainer.education?.length ? (
                      selectedTrainer.education.map((item, i) => (
                        <div className="trainer-info-card" key={i}>
                          <Text strong>{item.qualification}</Text>
                          <div style={{ margin: "4px 0" }}>
                            <Tag color="cyan">{item.institution}</Tag>
                            <Tag>{item.yearOfCompletion}</Tag>
                          </div>
                          {item.certificateName && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              <SafetyCertificateOutlined style={{ marginRight: 4 }} />
                              {item.certificateName} - {item.issuingOrganisation} ({item.yearObtained})
                            </Text>
                          )}
                        </div>
                      ))
                    ) : <Text type="secondary">No education data.</Text>}
                  </Card>
                </Col>
              </Row>
            </TabPane>

            <TabPane tab={<span><ScheduleOutlined /> Workshops</span>} key="workshops">
              <div style={{ marginTop: 16 }}>
                {selectedTrainer.workshops?.length ? (
                  selectedTrainer.workshops.map((item, i) => (
                    <div key={i} className="trainer-info-card" style={{ marginBottom: 16 }}>
                      <Space align="start" style={{ width: "100%", justifyContent: "space-between" }} wrap>
                        <div>
                          <Text strong style={{ fontSize: 15 }}>{item.title}</Text>
                          <div>
                            <Text type="secondary">@ {item.companyName}</Text>
                          </div>
                        </div>
                        <Tag color="blue">{item.totalParticipants} participants</Tag>
                      </Space>
                      <div style={{ margin: "10px 0" }}>
                        <Space size={[6, 6]} wrap>
                          {item.duration && <Tag icon={<ScheduleOutlined />}>{item.duration}</Tag>}
                          {item.location && <Tag icon={<EnvironmentOutlined />}>{item.location}</Tag>}
                          {item.industry && <Tag color="cyan">{item.industry}</Tag>}
                          {item.domain && <Tag color="geekblue">{item.domain}</Tag>}
                          {item.competency && <Tag color="purple">{item.competency}</Tag>}
                        </Space>
                      </div>
                      {item.summary && (
                        <Paragraph type="secondary" style={{ fontSize: 13 }}>{item.summary}</Paragraph>
                      )}
                      {item.photos?.length ? (
                        <Space size={8} wrap>
                          {item.photos.map((p, pi) => (
                            <Image key={pi} src={p} width={84} height={84} style={{ objectFit: "cover", borderRadius: 8 }} />
                          ))}
                        </Space>
                      ) : null}
                    </div>
                  ))
                ) : <Text type="secondary">No workshop data.</Text>}
              </div>
            </TabPane>

            <TabPane tab={<span><TrophyOutlined /> Awards & Media</span>} key="media">
              <Row gutter={[16, 16]} style={{ marginTop: 14 }}>
                <Col span={12}>
                  <Card
                    title={
                      <span className="trainer-card-title">
                        <TrophyOutlined style={{ color: "#fa8c16" }} />
                        {`Awards${selectedTrainer.awards?.length ? ` (${selectedTrainer.awards.length})` : ""}`}
                      </span>
                    }
                    bordered={false}
                    className="shadow-sm"
                  >
                    {selectedTrainer.awards?.length ? (
                      selectedTrainer.awards.map((item, i) => (
                        <div className="trainer-award-item" key={i}>
                          <div className="trainer-award-icon">
                            <TrophyOutlined />
                          </div>
                          <div className="trainer-award-body">
                            <Text strong>{item.title}</Text>
                            <div style={{ margin: "4px 0 6px" }}>
                              <Space size={[6, 6]} wrap>
                                {item.organisation && <Tag color="blue">{item.organisation}</Tag>}
                                {item.year && <Tag color="gold">{item.year}</Tag>}
                                {item.category && <Tag color="purple">{item.category}</Tag>}
                              </Space>
                            </div>
                            {item.description && (
                              <Paragraph type="secondary" style={{ fontSize: 12, margin: 0 }}>
                                {item.description}
                              </Paragraph>
                            )}
                          </div>
                        </div>
                      ))
                    ) : <Text type="secondary">No awards data.</Text>}
                  </Card>
                </Col>
                <Col span={12}>
                  <Card
                    title={<span className="trainer-card-title"><PictureOutlined style={{ color: "#722ed1" }} /> Media & Documents</span>}
                    bordered={false}
                    className="shadow-sm"
                  >
                    <div className="trainer-media-section-title"><PictureOutlined /> Gallery Photos</div>
                    {selectedTrainer.galleryPhotos?.length ? (
                      <Space size={8} wrap style={{ marginBottom: 16 }}>
                        {selectedTrainer.galleryPhotos.map((p, i) => (
                          <Image key={i} src={p} width={72} height={72} style={{ objectFit: "cover", borderRadius: 8 }} />
                        ))}
                      </Space>
                    ) : <div className="trainer-media-empty">No photos.</div>}

                    <div className="trainer-media-section-title"><VideoCameraOutlined /> Videos</div>
                    {selectedTrainer.videoUrls?.length ? (
                      <div style={{ marginBottom: 16 }}>
                        {selectedTrainer.videoUrls.map((v, i) => (
                          <Link className="trainer-link-chip" key={i} href={v} target="_blank">
                            <VideoCameraOutlined /> Video {i + 1}
                          </Link>
                        ))}
                      </div>
                    ) : <div className="trainer-media-empty">No videos.</div>}

                    <div className="trainer-media-section-title"><SafetyCertificateOutlined /> Certificates</div>
                    {selectedTrainer.certificates?.length ? (
                      <div style={{ marginBottom: 16 }}>
                        {selectedTrainer.certificates.map((c, i) => (
                          <Link className="trainer-link-chip" key={i} href={c} target="_blank">
                            <SafetyCertificateOutlined /> Certificate {i + 1}
                          </Link>
                        ))}
                      </div>
                    ) : <div className="trainer-media-empty">No certificates.</div>}

                    {selectedTrainer.profileDocument && (
                      <>
                        <div className="trainer-media-section-title"><FileTextOutlined /> Profile Document</div>
                        <Link className="trainer-link-chip" href={selectedTrainer.profileDocument} target="_blank">
                          <FileTextOutlined /> View Document
                        </Link>
                      </>
                    )}
                  </Card>
                </Col>
              </Row>
            </TabPane>

            <TabPane tab={<span><TranslationOutlined /> Languages</span>} key="languages">
              <div style={{ marginTop: 16 }}>
                <Card
                  title={<span className="trainer-card-title"><TranslationOutlined style={{ color: "#13a8a8" }} /> Languages Fluent In</span>}
                  bordered={false}
                  className="shadow-sm"
                >
                  {selectedTrainer.languagesFluent?.length ? (
                    <div>
                      {selectedTrainer.languagesFluent.map((item, i) => {
                        const level = (item.proficiencyLevel || "").toLowerCase();
                        const color = level.includes("native")
                          ? "#52c41a"
                          : level.includes("fluent")
                            ? "#1677ff"
                            : level.includes("basic")
                              ? "#fa8c16"
                              : "#8c8c8c";
                        return (
                          <span className="trainer-lang-chip" key={i}>
                            <TranslationOutlined style={{ color }} />
                            <Text strong>{item.language}</Text>
                            <Tag color={color} style={{ margin: 0 }}>{item.proficiencyLevel}</Tag>
                          </span>
                        );
                      })}
                    </div>
                  ) : <Text type="secondary">No language data.</Text>}
                </Card>
              </div>
            </TabPane>
          </Tabs>
        )}
      </Drawer>

      <Modal
        title="Add Trainer"
        open={addModalOpen}
        onCancel={() => setAddModalOpen(false)}
        onOk={() => addForm.submit()}
        confirmLoading={addSubmitting}
        okText="Add Trainer"
        width={760}
        destroyOnClose
      >
        <Form form={addForm} layout="vertical" onFinish={handleCreateTrainer}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label="Full Name" rules={[{ required: true, message: "Name is required" }]}>
                <Input placeholder="e.g. Aarav Sharma" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="mobile" label="Mobile" rules={[{ required: true, message: "Mobile is required" }]}>
                <Input placeholder="+91 98765 43210" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="subjectLine" label="Subject Line">
                <Input placeholder="e.g. Corporate Sales and Leadership Trainer" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="tagLine" label="Tag Line">
                <Input placeholder="e.g. Helping teams sell better" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="entityType" label="Entity Type">
                <Select placeholder="Select" allowClear>
                  <Option value="Individual">Individual</Option>
                  <Option value="Organisation">Organisation</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="trainerType" label="Trainer Type">
                <Input placeholder="e.g. Corporate Trainer" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="mode" label="Mode">
                <Select placeholder="Select" allowClear>
                  <Option value="Online">Online</Option>
                  <Option value="Offline">Offline</Option>
                  <Option value="Both">Both</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="city" label="City">
                <Input placeholder="City" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="state" label="State">
                <Input placeholder="State" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="country" label="Country">
                <Input placeholder="Country" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="pricePerHour" label="Price / Hour (₹)">
                <InputNumber min={0} style={{ width: "100%" }} placeholder="3000" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="experienceYears" label="Experience (Years)">
                <InputNumber min={0} style={{ width: "100%" }} placeholder="8" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="skills" label="Skills">
                <Select mode="tags" placeholder="Type and press enter" tokenSeparators={[","]} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="profileSummary" label="Profile Summary">
                <Input.TextArea rows={3} placeholder="Short professional summary" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="website" label="Website">
                <Input placeholder="https://example.com" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="linkedinProfile" label="LinkedIn">
                <Input placeholder="https://linkedin.com/in/..." />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="profilePhoto" label="Profile Photo URL">
                <Input placeholder="https://..." />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="availableThisWeek" label="Available This Week" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="offersTrialSessions" label="Offers Trial Sessions" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default TrainerForm;
